/**
 * Vignobles › Liste des domaines (/vignobles/domaines) — tableau filtrable + recherche,
 * ajout d'un domaine (saisie ou « Compléter avec l'IA » depuis le nom / l'URL du site).
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, Search } from 'lucide-react';
import { toast } from 'sonner';
import { vignoblesApi } from '../../services/api';
import { Button, Spinner, Tag, SegmentedControl } from '../../components/ui/kit';
import { SpecificityTags } from '../../components/wine/wine';

const STATUS_LABEL = { cave: 'En cave', deguste: 'Dégusté', a_decouvrir: 'À découvrir' };
const FILTERS = [{ value: '', label: 'Tous' }, { value: 'cave', label: 'En cave' }, { value: 'deguste', label: 'Dégustés' }, { value: 'a_decouvrir', label: 'À découvrir' }];

/** Boîte d'ajout d'un domaine avec pré-remplissage IA. */
function AddDomain({ onClose }) {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({ name: '', region: '', city: '', presentation: '', site_web: '' });
  const [busy, setBusy] = useState(false);

  const enrich = async () => {
    if (!query) return;
    setBusy(true);
    try {
      const d = await vignoblesApi.enrich(query);
      setForm({ name: d.name || query, region: d.region || '', city: d.city || '', presentation: d.presentation || '', site_web: d.site_web || '', gps: d.gps, appellations: d.appellations, specificities: d.specificities, dept: d.dept, logo_url: d.logo_url, phone: d.phone, email: d.email });
      toast.success('Fiche pré-remplie par l\'IA');
    } catch { toast.error('Échec IA'); } finally { setBusy(false); }
  };
  const save = async () => {
    await vignoblesApi.create(form);
    qc.invalidateQueries({ queryKey: ['domains'] });
    toast.success('Domaine ajouté');
    onClose();
  };

  return (
    <div className="vd-card p-4 mb-4" data-testid="add-domain">
      <p className="font-title text-xl mb-3">Ajouter un domaine</p>
      <div className="flex gap-2 mb-3">
        <input className="vd-input h-9 flex-1" placeholder="Nom ou URL du site du domaine" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="enrich-query" />
        <Button variant="secondary" className="h-9" onClick={enrich} disabled={busy} data-testid="enrich-btn"><Sparkles className="w-4 h-4" /> {busy ? '…' : "Compléter avec l'IA"}</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="vd-input h-9" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="domain-name" />
        <input className="vd-input h-9" placeholder="Région" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        <input className="vd-input h-9" placeholder="Ville" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <input className="vd-input h-9" placeholder="Site web" value={form.site_web} onChange={(e) => setForm({ ...form, site_web: e.target.value })} />
      </div>
      <textarea className="vd-input w-full mt-2 min-h-[60px]" placeholder="Présentation" value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })} />
      <div className="flex gap-2 mt-3">
        <Button variant="primary" onClick={save} disabled={!form.name} data-testid="domain-save">Enregistrer</Button>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
      </div>
    </div>
  );
}

export default function DomainesList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const { data: domains = [], isLoading } = useQuery({ queryKey: ['domains'], queryFn: () => vignoblesApi.domains() });

  const rows = useMemo(() => domains.filter((d) => (!status || d.status === status) && (!q || d.name.toLowerCase().includes(q.toLowerCase()) || (d.region || '').toLowerCase().includes(q.toLowerCase()))), [domains, status, q]);

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1100px]">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SegmentedControl options={FILTERS} value={status} onChange={setStatus} testId="domaines-filter" />
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-n500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="vd-input h-9 pl-9 w-[220px]" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="domaines-search" />
        </div>
        <Button variant="primary" className="h-9" onClick={() => setAdding((a) => !a)} data-testid="add-domain-btn"><Plus className="w-4 h-4" /> Ajouter un domaine</Button>
      </div>
      {adding && <AddDomain onClose={() => setAdding(false)} />}
      <div className="vd-card overflow-hidden">
        <table className="w-full text-[13.5px]" data-testid="domaines-table">
          <thead><tr className="text-left kicker">
            <th className="py-2.5 px-3">Domaine</th><th className="py-2.5 px-3">Région</th>
            <th className="py-2.5 px-3">Appellations</th><th className="py-2.5 px-3">Statut</th>
            <th className="py-2.5 px-3 text-right">Btl</th><th className="py-2.5 px-3">Spécificités</th>
            <th className="py-2.5 px-3 text-right">Dist.</th>
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="hair-t hover:bg-surface/60 cursor-pointer" onClick={() => navigate(`/vignobles/domaine/${d.id}`)} data-testid={`domaine-row-${d.id}`}>
                <td className="py-2.5 px-3 font-title text-lg">{d.name}{d.hearts > 0 && <span className="text-gold ml-1">{'\u2665'.repeat(d.hearts)}</span>}</td>
                <td className="py-2.5 px-3 text-n700">{d.region}</td>
                <td className="py-2.5 px-3 text-n600">{(d.appellations || []).join(', ')}</td>
                <td className="py-2.5 px-3"><Tag tone={d.status === 'cave' ? 'fill' : 'accent'}>{STATUS_LABEL[d.status]}</Tag></td>
                <td className="py-2.5 px-3 text-right nums">{d.bottles}</td>
                <td className="py-2.5 px-3"><SpecificityTags keys={d.specificities || []} max={2} /></td>
                <td className="py-2.5 px-3 text-right nums text-n600">{d.distance_km ? `${d.distance_km} km` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
